import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Row, Col } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, { PaginationProvider, PaginationListStandalone, SizePerPageDropdownStandalone } from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import Cookies from 'js-cookie';
import { Fetcher } from '../../../Helpers/fetcher';
import { Notify } from '../../../Helpers/notification';
import { useHistory } from 'react-router-dom';

// Sub-superadmin's own flash messages -- entirely separate from the
// superadmin-owned messages that real end users/resellers see (FlashMessage/index.js).
// Whatever a sub-superadmin adds/edits here never reaches any end user.
const SubAdminFlashMessage = ({ app }) => {
    const history = useHistory();
    const userId = app.userId;
    const [messages, setMessages] = useState([]);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [modal, setModal] = useState({ show: false, mode: 'view', data: null });
    const [loading, setLoading] = useState(true);

    const fetchMessages = () => {
        setLoading(true);
        Fetcher.get(`${app.apiPath}/api/obd/get/own/flash/messages/${userId}`)
            .then(res => {
                setMessages(Array.isArray(res) ? res : []);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching flash messages:', err);
                Notify.notify('Failed to fetch flash messages');
                setLoading(false);
            });
    };

    useEffect(() => {
        if (app.role !== 'ROLE_SUB_SADMIN') {
            history.replace('/');
        }
    }, [app.role, history]);

    useEffect(() => {
        const savedSize = Cookies.get('subAdminFlashMessagesSizePerPage');
        if (savedSize) setSizePerPage(Number(savedSize));
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSizePerPageChange = (newSize) => {
        setSizePerPage(newSize);
        Cookies.set('subAdminFlashMessagesSizePerPage', newSize);
    };

    const openAdd = () => {
        setModal({ show: true, mode: 'add', data: { moduleName: '', action: 'view', message: '' } });
    };

    const openEdit = (row) => {
        setModal({ show: true, mode: 'edit', data: { ...row } });
    };

    const closeModal = () => setModal({ show: false, mode: 'view', data: null });

    const handleSave = () => {
        const { mode, data } = modal;
        if (!data || !data.message?.trim() || !data.moduleName?.trim()) {
            Notify.notify('Module name and message are required');
            return;
        }

        if (mode === 'add') {
            const payload = {
                moduleName: data.moduleName,
                action: data.action || 'view',
                message: data.message,
                createdBy: userId
            };

            Fetcher.post(`${app.apiPath}/api/obd/add/own/flash/message`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
            })
                .then(res => {
                    Notify.notify(res.message || 'Message added successfully');
                    closeModal();
                    fetchMessages();
                })
                .catch(err => {
                    console.error('Error adding message:', err);
                    Notify.notify('Failed to add message');
                });
        } else {
            const payload = {
                id: data.id,
                moduleName: data.moduleName,
                action: data.action,
                message: data.message,
                createdBy: userId
            };

            Fetcher.post(`${app.apiPath}/api/obd/edit/own/flash/message`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
            })
                .then(res => {
                    Notify.notify(res.message || 'Message updated successfully');
                    closeModal();
                    fetchMessages();
                })
                .catch(err => {
                    console.error('Error updating message:', err);
                    Notify.notify('Failed to update message');
                });
        }
    };

    const columns = [
        { dataField: 'id', text: 'ID', sort: true },
        { dataField: 'moduleName', text: 'Module' },
        { dataField: 'action', text: 'Action' },
        {
            dataField: 'message',
            text: 'Message',
            formatter: (_, row) => (
                <Button size="sm" onClick={() => openEdit(row)}>View / Edit</Button>
            )
        }
    ];

    const { SearchBar, ClearSearchButton } = Search;
    const paginationOptions = {
        custom: true,
        totalSize: messages.length,
        sizePerPage,
        sizePerPageList: [10, 25, 50, 100],
        onSizePerPageChange: handleSizePerPageChange
    };

    return (
        <Card>
            <Card.Header>My Flash Messages</Card.Header>
            <Card.Body>
                <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                    {({ paginationProps, paginationTableProps }) => (
                        <ToolkitProvider keyField="id" data={messages} columns={columns} search bootstrap4>
                            {props => (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            Show <SizePerPageDropdownStandalone {...paginationProps} />
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <SearchBar {...props.searchProps} />
                                            <ClearSearchButton className="btn btn-primary mb-2" {...props.searchProps} />
                                            <Button variant="primary" onClick={openAdd}>+ Add Message</Button>
                                        </div>
                                    </div>

                                    <BootstrapTable
                                        bordered
                                        hover
                                        condensed
                                        wrapperClasses="table-responsive"
                                        classes="flash-messages-table"
                                        {...props.baseProps}
                                        {...paginationTableProps}
                                        noDataIndication="No messages found"
                                    />

                                    <PaginationListStandalone {...paginationProps} />
                                </>
                            )}
                        </ToolkitProvider>
                    )}
                </PaginationProvider>

                <Modal show={modal.show} onHide={closeModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{modal.mode === 'add' ? 'Add Flash Message' : 'Message Details'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col sm={6}>
                                <Form.Group>
                                    <Form.Label>Module Name</Form.Label>
                                    <Form.Control
                                        value={modal.data?.moduleName || ''}
                                        disabled={modal.mode === 'edit'}
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, moduleName: e.target.value } })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={6}>
                                <Form.Group>
                                    <Form.Label>Action</Form.Label>
                                    <Form.Control
                                        value={modal.data?.action || ''}
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, action: e.target.value } })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group>
                            <Form.Label>Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={modal.data?.message || ''}
                                onChange={e => setModal({ ...modal, data: { ...modal.data, message: e.target.value } })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeModal}>Close</Button>
                        <Button variant="primary" onClick={handleSave}>Save</Button>
                    </Modal.Footer>
                </Modal>

                {loading && <div className="fetching-data"><img alt="" src="/assets/loading.gif" /></div>}
            </Card.Body>
        </Card>
    );
};

export default SubAdminFlashMessage;
